/**
 * All MappingPhase state, handlers, and derived values — extracted so the
 * component itself stays a thin render function under the ESLint
 * max-lines-per-function limit (mirrors the orchestration-hook pattern used
 * elsewhere in apps/web, e.g. use-media-units-page.ts).
 */
import * as React from 'react';
import {
  SKIP_VALUE,
  TYP_NOSNIKA_SOURCE_COLUMN,
  getUnmappedRequiredFields,
  applyMappingChange,
} from './mapping-phase-parts';
import { GROUP_ORDER, type FieldGroupKey } from './import-field-group-order';
import {
  buildMappingTransformLabels,
  applyPatternTextChange,
  applyBlankTargetChange,
  applySuggestedPatternOnFirstEdit,
  applyTransformModeSwitch,
} from './mapping-phase-transform-editor';
import type { TransformMode } from './mapping-transforms-mode-toggle';
import {
  buildValueMapEditorLabels,
  applyValueMapEntryChange,
  hasUnmappedValueMapEntries,
} from './mapping-phase-value-map-editor';
import {
  buildConstantEditorLabels,
  buildConstantTypeAllowedValues,
  buildMissingRequiredFieldsLabels,
  applyConstantAssignment,
  buildConstantMappingItems,
  hasResolvedConstantValue,
} from './mapping-phase-constant-editor';
import {
  buildAutoSuggestBannerLabels,
  buildSourceBarLabels,
} from './mapping-phase-source-bar-section';
import { collectMappedFieldKeys, isMediaTypeFieldCovered } from '../lib/collect-mapped-field-keys';
import { computeMissingRequiredFieldKeys } from '../lib/mapping-phase-wizard-helpers';
import type {
  ColumnMappingItem,
  FieldOptionItem,
  MappingPhaseLabels,
  MappingPhaseProps,
} from './mapping-phase';

function getGroupLabel(group: FieldGroupKey, labels: MappingPhaseLabels): string {
  const map: Record<FieldGroupKey, string> = {
    general: labels.groupGeneral,
    physical: labels.groupPhysical,
    mural_flags: labels.groupMuralFlags,
    digital: labels.groupDigital,
    internal_cost: labels.groupInternalCost,
    client_price: labels.groupClientPrice,
  };
  return map[group];
}

/** Returns false when a per-file override is active; otherwise blocks when required fields are
 * unresolved OR a value-map column has a distinct source value not yet covered by its map. */
function computeIsBlocked(
  mediaTypeOverride: string | undefined,
  unmappedRequired: FieldOptionItem[],
  unresolvedMissingRequired: FieldOptionItem[],
  hasUnmappedValueMap: boolean,
): boolean {
  if (hasUnmappedValueMap) return true;
  if (mediaTypeOverride) return false;
  return unmappedRequired.length > 0 || unresolvedMissingRequired.length > 0;
}

// eslint-disable-next-line max-lines-per-function -- orchestrates MappingPhase's full state/handler/derived-value surface; splitting further would scatter one cohesive screen's logic across many hooks
export function useMappingPhaseState(props: MappingPhaseProps) {
  const {
    columnMappings,
    fieldOptions,
    suppliers,
    supplierName,
    hasSavedMapping,
    missingRequiredFieldKeys,
    labels,
    onNext,
    mediaTypeOverride,
    columnSamples,
    columnDistinctValues,
    transformSuggestions,
    manualFallbackMapping,
    headerRowIndex,
  } = props;

  const [mappings, setMappings] = React.useState<ColumnMappingItem[]>(() =>
    columnMappings.map((m) => ({ ...m })),
  );
  const [selectedSupplierId, setSelectedSupplierId] = React.useState<string | null>(
    () => suppliers.find((s) => s.name === supplierName)?.id ?? null,
  );
  const [editingColumnId, setEditingColumnId] = React.useState<string | null>(null);
  const [savedConfigCleared, setSavedConfigCleared] = React.useState(false);
  const [editingConstantKey, setEditingConstantKey] = React.useState<string | null>(null);
  const [constantAssignments, setConstantAssignments] = React.useState<Record<string, string>>({});

  const handleClearSavedConfig = React.useCallback(() => {
    setSavedConfigCleared(true);
    setMappings((manualFallbackMapping ?? columnMappings).map((m) => ({ ...m })));
  }, [manualFallbackMapping, columnMappings]);

  const handleEditConstant = React.useCallback(
    (fieldKey: string) => setEditingConstantKey(fieldKey),
    [],
  );

  const handleConstantChange = React.useCallback((fieldKey: string, value: string) => {
    setConstantAssignments((prev) => applyConstantAssignment(prev, fieldKey, value));
  }, []);

  const handleValueMapEntryChange = React.useCallback((id: string, from: string, to: string) => {
    setMappings((prev) => applyValueMapEntryChange(prev, id, from, to));
  }, []);

  const handleMappingChange = React.useCallback((id: string, value: string) => {
    setMappings((prev) => applyMappingChange(prev, id, value, SKIP_VALUE));
  }, []);

  const handleEditPattern = React.useCallback(
    (id: string) => {
      setMappings((prev) => applySuggestedPatternOnFirstEdit(prev, id, transformSuggestions));
      setEditingColumnId(id);
    },
    [transformSuggestions],
  );

  const handlePatternTextChange = React.useCallback(
    (pattern: string) => {
      if (editingColumnId == null) return;
      setMappings((prev) => applyPatternTextChange(prev, editingColumnId, pattern));
    },
    [editingColumnId],
  );

  const handleBlankTargetChange = React.useCallback(
    (id: string, blankIndex: number, value: string | null) => {
      setMappings((prev) => applyBlankTargetChange(prev, id, blankIndex, value));
    },
    [],
  );

  const handleTransformModeChange = React.useCallback((id: string, mode: TransformMode) => {
    setMappings((prev) => applyTransformModeSwitch(prev, id, mode));
  }, []);

  // Single source of truth for "the saved config no longer applies" — both the
  // supplier-select visibility (Bug 5/86can7jwy: "Wyczyść konfigurację" cleared the
  // table client-side but never let the operator supply a supplier again) and handleNext's
  // save-for-supplier gate below must key off this, not the raw `hasSavedMapping` prop alone.
  const effectiveHasSavedMapping = hasSavedMapping && !savedConfigCleared;

  const handleNext = React.useCallback(() => {
    const saveFor =
      !effectiveHasSavedMapping && selectedSupplierId != null ? selectedSupplierId : undefined;
    onNext([...mappings, ...buildConstantMappingItems(constantAssignments)], saveFor);
  }, [effectiveHasSavedMapping, selectedSupplierId, onNext, mappings, constantAssignments]);

  const transformLabels = React.useMemo(() => buildMappingTransformLabels(labels), [labels]);
  const valueMapLabels = React.useMemo(() => buildValueMapEditorLabels(labels), [labels]);
  const constantLabels = React.useMemo(() => buildConstantEditorLabels(labels), [labels]);
  const constantTypeAllowedValues = React.useMemo(
    () => buildConstantTypeAllowedValues(labels),
    [labels],
  );
  const missingRequiredLabels = React.useMemo(
    () => buildMissingRequiredFieldsLabels(labels),
    [labels],
  );
  const autoSuggestLabels = React.useMemo(() => buildAutoSuggestBannerLabels(labels), [labels]);
  const sourceBarLabels = React.useMemo(
    () => buildSourceBarLabels(labels, headerRowIndex),
    [labels, headerRowIndex],
  );
  const editingMapping = React.useMemo(
    () => mappings.find((m) => m.id === editingColumnId) ?? null,
    [mappings, editingColumnId],
  );

  // Includes pattern-transform per-blank targets (not just plain mappedTo) so splitting a
  // required field (e.g. latitude/longitude via a combined-GPS pattern) live-clears it from
  // both the unmapped- and missing-required sections below instead of blocking Next forever
  // (custom-supplier-import-transforms: missingRequiredFieldKeys is a snapshot computed once
  // from the initial suggested/saved mapping, so it must be re-checked against the operator's
  // LIVE mappings state, not trusted as final).
  const mappedSystemFieldKeys = React.useMemo(() => collectMappedFieldKeys(mappings), [mappings]);
  const supplierCoveredByCombobox = selectedSupplierId != null || effectiveHasSavedMapping;
  // `missingRequiredFieldKeys` (prop) is a one-time snapshot the parent computed from the
  // INITIAL suggested/saved mapping. Trusting it forever caused two symptoms of the same root
  // bug (CU-86can7jwy Bug 4 + its "Wyczyść konfigurację" sibling, now collapsed into one path):
  // a field the snapshot correctly excluded (because a column satisfied it AT THE TIME) stays
  // stuck in the no-affordance "unmapped-required" bucket forever once the operator later (a)
  // clears the saved config, or (b) manually re-maps that one satisfying column to "Pomiń tę
  // kolumnę" — without ever touching "Wyczyść konfigurację". Union the snapshot with a LIVE
  // recomputation from the current `mappings` state on every render: the snapshot still seeds
  // any field a test/caller explicitly flagged missing (e.g. `supplier`, deliberately excluded
  // from computeMissingRequiredFieldKeys because it's normally resolved via the supplier
  // combobox instead of a column), while the live half guarantees any field that is NOW
  // genuinely unmapped always gets a resolution path via the constant editor, regardless of
  // which bucket the initial snapshot placed it in.
  const effectiveMissingRequiredFieldKeys = React.useMemo(() => {
    const liveMissing = computeMissingRequiredFieldKeys(mappings, fieldOptions);
    const seed = missingRequiredFieldKeys ?? [];
    return Array.from(new Set([...seed, ...liveMissing]));
  }, [mappings, fieldOptions, missingRequiredFieldKeys]);
  const unmappedRequiredFields = React.useMemo(
    () =>
      getUnmappedRequiredFields(
        fieldOptions,
        mappedSystemFieldKeys,
        effectiveMissingRequiredFieldKeys,
        supplierCoveredByCombobox,
        mediaTypeOverride,
      ),
    [
      fieldOptions,
      mappedSystemFieldKeys,
      effectiveMissingRequiredFieldKeys,
      supplierCoveredByCombobox,
      mediaTypeOverride,
    ],
  );
  // Excludes the media-type field once a file-level type override is active — that override
  // auto-resolves "Typ nosnika" per row, so it must not keep surfacing in this "missing from the
  // file entirely" bucket alongside genuinely unresolved fields (see isMediaTypeFieldCovered doc).
  const missingRequiredFields = React.useMemo(
    () =>
      fieldOptions.filter(
        (o) =>
          effectiveMissingRequiredFieldKeys.includes(o.key) &&
          !mappedSystemFieldKeys.has(o.key) &&
          !isMediaTypeFieldCovered(o.key, mediaTypeOverride),
      ),
    [fieldOptions, effectiveMissingRequiredFieldKeys, mappedSystemFieldKeys, mediaTypeOverride],
  );
  const unresolvedMissingRequiredFields = React.useMemo(
    () =>
      missingRequiredFields.filter((o) => !hasResolvedConstantValue(constantAssignments[o.key])),
    [missingRequiredFields, constantAssignments],
  );
  const hasUnmappedValueMap = React.useMemo(
    () => hasUnmappedValueMapEntries(mappings, columnDistinctValues, columnSamples),
    [mappings, columnDistinctValues, columnSamples],
  );
  const isBlocked = computeIsBlocked(
    mediaTypeOverride,
    unmappedRequiredFields,
    unresolvedMissingRequiredFields,
    hasUnmappedValueMap,
  );

  // Memoized like every other derived value in this hook (see fix note above) — this is
  // recomputed from the full FIELD_OPTIONS catalog and re-passed by reference into every mapped
  // row's Select, so an unmemoized version produced a fresh array/object graph on every render.
  const groupedOptions = React.useMemo(
    () =>
      GROUP_ORDER.map((groupKey) => ({
        groupKey,
        label: getGroupLabel(groupKey, labels),
        options: fieldOptions.filter((o) => o.group === groupKey),
      })).filter((g) => g.options.length > 0),
    [fieldOptions, labels],
  );

  const visibleMappings = React.useMemo(
    () =>
      mediaTypeOverride
        ? mappings.filter((m) => m.sourceColumn !== TYP_NOSNIKA_SOURCE_COLUMN)
        : mappings,
    [mediaTypeOverride, mappings],
  );
  const hasCollapsedTypRow = React.useMemo(
    () =>
      mediaTypeOverride != null &&
      mappings.some((m) => m.sourceColumn === TYP_NOSNIKA_SOURCE_COLUMN),
    [mediaTypeOverride, mappings],
  );

  return {
    mappings,
    selectedSupplierId,
    setSelectedSupplierId,
    savedConfigCleared,
    effectiveHasSavedMapping,
    editingConstantKey,
    setEditingConstantKey,
    constantAssignments,
    handleClearSavedConfig,
    handleEditConstant,
    handleConstantChange,
    handleValueMapEntryChange,
    handleMappingChange,
    handleEditPattern,
    handlePatternTextChange,
    handleBlankTargetChange,
    handleTransformModeChange,
    handleNext,
    setEditingColumnId,
    transformLabels,
    valueMapLabels,
    constantLabels,
    constantTypeAllowedValues,
    missingRequiredLabels,
    autoSuggestLabels,
    sourceBarLabels,
    editingMapping,
    unmappedRequiredFields,
    missingRequiredFields,
    isBlocked,
    groupedOptions,
    visibleMappings,
    hasCollapsedTypRow,
    // Exposed so the output preview can mirror the same "is supplier covered without a
    // mapped column" check (item #1: supplier chosen via the combobox must not surface a
    // false "supplier is required" preview error) instead of recomputing it separately.
    supplierCoveredByCombobox,
  };
}
